import React, { useEffect, useState } from 'react';
import './ReceptionFromSupplier.less';
import { LeftOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Form, Input, Space } from 'antd';
import { useHistory } from 'react-router-dom';
import { useAuth } from '@isocel/front-common';

import { BaseModel, PackagingFlowDirection, StoreUser } from '../../../queries/api/types';
import { fetchTransporters } from '../../../queries/api/transporter';
import { useSupplierByStore } from '../../../queries/api/supplier';
import { usePackagingFlow } from '../../../context/PackagingFlowContext';
import { usePackagingConfig } from '../../../queries/package';
import { useSupplierByOrganization } from '../../../queries/palette';

const ReceptionFromSupplier: React.FC = () => {
    const history = useHistory();
    const [searchSupplier, setSearchSupplier] = useState('');
    const [searchTransporter, setSearchTransporter] = useState('');
    const [filteredSuppliers, setFilteredSuppliers] = useState<BaseModel[]>([]);
    const [filteredTransporters, setFilteredTransporters] = useState<BaseModel[]>([]);
    const [voucher, setVoucher] = useState('');
    const [suppliers, setSuppliers] = useState<BaseModel[]>([]);
    const [transporters, setTransporters] = useState<BaseModel[]>([]);
    const {
        setPackageFlow,
        setVoucher: setContextVoucher,
        setRecipientLabel,
        setTransporterId,
        setSupplierId,
        setSupplierName,
        setFlowType,
        setTransporterName,
    } = usePackagingFlow();
    const { data: packageConfig } = usePackagingConfig();
    const [form] = Form.useForm();
    const { user } = useAuth();
    const storeId = (user as StoreUser)?.store || '';
    const supplierByStoreData = useSupplierByStore(storeId || '', user?.storeDetails?.organization);
    const supplierByOrganizationData = useSupplierByOrganization(user?.organization?.id || '');
    const supplierData = storeId ? supplierByStoreData.data : supplierByOrganizationData.data;

    useEffect(() => {
        setFlowType(PackagingFlowDirection.ReceivedFromSupplier);

        if (supplierData) {
            setSuppliers(supplierData);
            setFilteredSuppliers(supplierData);
        }

        const fetchTransportersData = async () => {
            const data = await fetchTransporters();
            setTransporters(data);
            setFilteredTransporters(data);
        };

        fetchTransportersData();
    }, [setFlowType, supplierData]);

    // Only allow selection from list for supplier
    const handleSelectSupplier = (value: string, option: any) => {
        setSearchSupplier(value);
        setSupplierId(option.id);
        setSupplierName(value);
        form.setFieldValue('supplier', value);
    };

    const handleSearchSupplierChange = (value: string) => {
        setSearchSupplier(value);
        const filtered = suppliers.filter((supplier) => supplier.name.toLowerCase().includes(value.toLowerCase()));
        setFilteredSuppliers(filtered);

        // If input doesn't match any supplier, clear selection
        if (!filtered.some((s) => s.name === value)) {
            setSupplierId('');
            setSupplierName('');
            form.setFieldValue('supplier', '');
        }
    };

    // Only allow selection from list for transporter
    const handleSelectTransporter = (value: string, option: any) => {
        setSearchTransporter(value);
        setTransporterId(option.id ?? null);
        setTransporterName(value);
        form.setFieldValue('transporter', value);
    };

    const handleSearchTransporterChange = (value: string) => {
        setSearchTransporter(value);
        const filtered = transporters.filter((transporter) =>
            transporter.name.toLowerCase().includes(value.toLowerCase())
        );
        setFilteredTransporters(filtered);

        // If input doesn't match any transporter, clear selection
        if (!filtered.some((t) => t.name === value)) {
            setTransporterId('');
            setTransporterName('');
            form.setFieldValue('transporter', '');
        }
    };

    const handleVoucherChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setVoucher(e.target.value);
        setContextVoucher(e.target.value);
    };

    const handleNextClick = async () => {
        form.validateFields().then(async () => {
            if (packageConfig) {
                const flowItems = packageConfig.packages.map((pkg) => ({
                    package: pkg.id,
                    shippedQuantity: 0,
                    goodCount: 0,
                    badCount: 0,
                }));

                setPackageFlow({
                    sender: '',
                    recipient: '',
                    user: '',
                    loadingSession: '',
                    flowType: PackagingFlowDirection.ReceivedFromSupplier,
                    packages: flowItems,
                });
            }
            setRecipientLabel('');
            history.push(`/recipientTrack/${storeId}`);
        });
    };

    return (
        <div className="reception-from-supplier-page">
            <div className="title">
                <LeftOutlined className="back-icon" onClick={() => history.goBack()} />
                <h2>Veuillez saisir les informations de la livraison</h2>
            </div>
            <Form
                form={form}
                name="reception-from-supplier-form"
                layout="vertical"
                onFinish={handleNextClick}
                autoComplete="off"
            >
                <Form.Item
                    name="supplier"
                    rules={[
                        { required: true, message: 'Veuillez sélectionner un transporteur' }, // swapped error message
                        {
                            validator: async (_, value) =>
                                filteredSuppliers.some((s) => s.name === value)
                                    ? await Promise.resolve()
                                    : await Promise.reject(new Error('')),
                        },
                    ]}
                >
                    <label htmlFor="supplier">Libellé du transporteur</label> {/* swapped label */}
                    <AutoComplete
                        className="input-field"
                        placeholder="Saisir le transporteur"
                        notFoundContent="Aucun résultat"
                        value={searchSupplier}
                        onChange={handleSearchSupplierChange}
                        onSelect={handleSelectSupplier}
                        options={filteredSuppliers.map((supplier) => ({
                            value: supplier.name,
                            id: supplier.id,
                            key: supplier.id,
                        }))}
                        filterOption={false}
                    />
                </Form.Item>
                <Form.Item
                    name="transporter"
                    rules={[
                        { required: true, message: 'Veuillez sélectionner un fournisseur' }, // swapped error message
                        {
                            validator: async (_, value) =>
                                filteredTransporters.some((t) => t.name === value)
                                    ? await Promise.resolve()
                                    : await Promise.reject(new Error('')),
                        },
                    ]}
                >
                    <label htmlFor="transporter">Libellé du fournisseur</label> {/* swapped label */}
                    <AutoComplete
                        className="input-field"
                        placeholder="Saisir le fournisseur"
                        notFoundContent="Aucun résultat"
                        value={searchTransporter}
                        onChange={handleSearchTransporterChange}
                        onSelect={handleSelectTransporter}
                        options={filteredTransporters.map((transporter) => ({
                            value: transporter.name,
                            id: transporter._id,
                            key: transporter._id,
                        }))}
                        filterOption={false}
                    />
                </Form.Item>
                <Form.Item name="voucher">
                    <label htmlFor="voucher">Numéro de bon de transport (optionnel)</label>
                    <Input
                        placeholder="Saisir le numéro de bon de transport"
                        value={voucher}
                        onChange={handleVoucherChange}
                        className="w-full"
                    />
                </Form.Item>
                <Form.Item>
                    <Space>
                        <Button type="primary" htmlType="submit" disabled={!searchSupplier || !searchTransporter}>
                            Suivant
                        </Button>
                    </Space>
                </Form.Item>
            </Form>
        </div>
    );
};

export default ReceptionFromSupplier;
